<template>
    <Accordion
        class="component-jobs-job"
        :class="classAccordion"
        :is-open="isOpen"
        @toggle="toggle"
    >
        <template #title>
            <div class="title">
                <ThTitle
                    class="th-title"
                    tag="H2"
                    color="var(--blue)"
                    size="SMALL"
                    >{{ job.title }}
                </ThTitle>

                <button class="view-job-button">
                    View job

                    <Icon
                        class="icon"
                        icon="angle-down"
                    />
                </button>
            </div>

            <Transition
                name="slide"
                mode="out-in"
            >
                <div
                    v-if="!isOpen"
                    class="subtitle"
                >
                    <span>
                        {{ job.contractType }}
                    </span>

                    <span>
                        {{ job.location }}
                    </span>

                    <span>
                        {{ job.salary }}
                    </span>

                    <span class="date-posted">
                        Job posted:
                        {{ job.postedDate }}
                    </span>
                </div>

                <div
                    v-else
                    class="subtitle stub"
                ></div>
            </Transition>
        </template>

        <template #default>
            <div class="details">
                <table class="summary">
                    <tbody>
                        <tr>
                            <th class="header">Contract type</th>
                            <td>{{ job.contractType }}</td>
                        </tr>

                        <tr>
                            <th class="header">Location</th>
                            <td>{{ job.location }}</td>
                        </tr>

                        <tr>
                            <th class="header">Salary</th>
                            <td>{{ job.salary }}</td>
                        </tr>

                        <tr>
                            <th class="header">Job posted</th>
                            <td>{{ job.postedDate }}</td>
                        </tr>

                        <tr v-if="job.startDate">
                            <th class="header">Start date</th>
                            <td>{{ job.startDate }}</td>
                        </tr>

                        <tr v-if="job.closingDate">
                            <th class="header">Closing date</th>
                            <td>{{ job.startDate }}</td>
                        </tr>

                        <tr v-if="job.vacancyReference">
                            <th class="header">Vacancy reference</th>
                            <td>{{ job.vacancyReference }}</td>
                        </tr>
                    </tbody>
                </table>

                <div class="full-details">
                    <ContentComponentItem
                        v-for="(item, index) in job.content"
                        :key="index"
                        :data="item"
                        background="WHITE"
                    />
                </div>

                <div class="apply-button-wrapper">
                    <ThButton :href="`mailto:${job.applicationRecipientEmail}`">
                        Apply now
                    </ThButton>
                </div>
            </div>
        </template>
    </Accordion>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { ComponentJobsJobFragment } from '@/graphql/api'

import Accordion from '@/components/layout/Accordion.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import Icon from '@/components/ui/icon/Icon.vue'
import ContentComponentItem from './ContentComponentItem.vue'

const { job } = defineProps<{
    job: ComponentJobsJobFragment
}>()

const isOpen = ref(job.expandedByDefault)
const toggle = () => {
    isOpen.value = !isOpen.value
}

const classAccordion = computed(() => ({
    'open': isOpen.value,
}))
</script>

<style lang="scss" scoped>
.component-jobs-job {
    --time-close: 0.12s;

    padding: 16px 0;
    border-bottom: $border-grey;

    &:first-child {
        border-top: $border-grey;
    }

    &.open {
        .icon {
            transform: rotateZ(180deg);
            transition: transform var(--time-open)
                var(--timing-function-easing-emphasized);
        }
    }

    .title {
        display: flex;
        align-items: center;

        @include min-width(768) {
            padding: 0 0 0 20px;
        }

        .th-title {
            margin: 0;
            font-size: 1.2rem;

            @include min-width(768) {
                font-size: 1.5rem;
            }
        }

        .view-job-button {
            display: flex;
            flex-shrink: 0;
            align-items: center;
            gap: 8px;
            margin-left: auto;
            text-transform: uppercase;
            height: 40px;
            border-radius: 20px;
            padding: 0 16px;
            user-select: none;
            font-family: $effra-font-stack;
            letter-spacing: 0.05em;
            font-size: 0.85rem;
            font-weight: 400;

            &:hover {
                background-color: $grey-200;
            }

            .icon {
                font-size: 1rem;
                margin-left: auto;
                display: block;
                transition: transform var(--time-close)
                    var(--timing-function-easing-emphasized);
            }
        }
    }

    .subtitle {
        display: none;
        height: 20px;
        align-items: center;
        gap: 40px;
        color: $grey-500;
        padding: 10px 0 0 20px;

        .date-posted {
            margin-left: auto;
        }

        @include min-width(1024) {
            display: flex;
        }
    }

    .details {
        margin-top: 20px;
        .summary,
        .full-details {
            margin-bottom: 16px;
        }

        .summary {
            width: 100%;
            margin: 0 auto 20px;
            padding: 0 0 20px 0;
            display: block;
            border-bottom: 1px solid $grey-300;

            @include min-width(768) {
                padding: 0 0 20px 20px;
            }

            .header {
                min-width: 150px;
                padding: 6px 0;
            }
        }
    }

    .apply-button-wrapper {
        width: 100%;
        max-width: 680px;
        margin: 0 auto;
        padding: 0 0 40px;
    }

    .slide-enter-active,
    .slide-leave-active {
        transition: all var(--time-close) ease;
    }

    .slide-enter-from,
    .slide-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }
}
</style>
